// ComposerAttachButton — the "+" add-a-source affordance PowerComposer's
// `leadingSlot` renders, pure. Extracted so the NASCENT composer
// (WorkspaceLanding's hero) and the ONGOING composer (the chat foot) share
// the exact same trigger + menu instead of two hand-rolled copies drifting
// apart (F10 parity). Two doors, matching the owner-approved landing design:
// upload a NEW source (the real ingest picker) or choose an EXISTING one (a
// nested pick list — the same `pickMenuItems` vocabulary the composer's
// `@`-mention menu and ContextCard already share). The pick list offers
// SOURCES only (D3, owner decision 2026-10-06): attaching a material by
// mistake made the agent generate from it. `@` still references any kind.
//
// Pure/props-driven: no I/O, no state. `onUpload` opens the host's real file
// picker; `onPick` reports a chosen candidate up (the caller decides whether
// that means grounding a nascent conversation or mentioning into an ongoing
// one — same shape, different wiring, never a different component).

import { useLingui } from "@lingui/react/macro";
import { pickMenuItems } from "../../ContextCard/ContextCard.js";
import type { ContextItem } from "../../ContextCard/types.js";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import { Menu, type MenuItem } from "../../Menu/Menu.js";
import {
    type ComposerResourceRef,
    type ComposerSourceRef,
    isComposerSourceRef,
} from "./types.js";

export type ComposerAttachButtonProps = {
    // The `@`-mentionable resource pool — the SAME live list the composer's
    // own mention menu offers. Only its sources are offered here.
    readonly candidates: readonly ComposerResourceRef[];
    // What is already attached/grounded, so an already-picked candidate shows
    // disabled with a check glyph (error prevention, Nielsen #5) rather than
    // being offered twice.
    readonly contextItems: readonly ContextItem[];
    // Opens the real ingest file picker — the host's one upload path.
    readonly onUpload: () => void;
    // An existing candidate was picked from the "Choose an existing source"
    // submenu.
    readonly onPick: (source: ComposerSourceRef) => void;
    readonly disabled?: boolean;
};

// The "Choose an existing source" rows: the composer's pool narrowed to
// sources (D3), then the shared row builder. Pure, so the sources-only
// rule is pinned without rendering the menu.
export const attachPickItems = (
    candidates: readonly ComposerResourceRef[],
    contextItems: readonly ContextItem[],
    onPick: (source: ComposerSourceRef) => void,
): readonly MenuItem[] =>
    pickMenuItems(candidates.filter(isComposerSourceRef), contextItems, onPick);

export const ComposerAttachButton = ({
    candidates,
    contextItems,
    onUpload,
    onPick,
    disabled = false,
}: ComposerAttachButtonProps) => {
    const { t } = useLingui();

    const pickItems = attachPickItems(candidates, contextItems, onPick);
    const items: readonly MenuItem[] = [
        {
            _tag: "item",
            label: t`Upload a source from file`,
            icon: <Icon name="upload" size="xs" />,
            onSelect: onUpload,
        },
        {
            _tag: "submenu",
            label: t`Choose an existing source`,
            icon: <Icon name="search" size="xs" />,
            items:
                pickItems.length === 0
                    ? [
                          {
                              _tag: "label",
                              label: t`Nothing else to pick`,
                          } satisfies MenuItem,
                      ]
                    : pickItems,
        },
    ];

    return (
        <Menu
            trigger={
                <IconButton
                    label={t`Add a source`}
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                >
                    <Icon name="paperclip" size="sm" />
                </IconButton>
            }
            items={items}
        />
    );
};
